// Region detection for the multi-domain setup (253 / 206 / 425 Gravel).
//
// All three domains serve the same React app. We infer the visitor's
// region from the hostname so the wordmark, copy, and defaults can match
// how locals refer to their part of the Puget Sound metro:
//
//   253gravel.com → Pierce / South Sound (default)
//   206gravel.com → Seattle proper
//   425gravel.com → Eastside
//
// A `?region=206` query override is supported for local testing where the
// hostname is always localhost / the preview domain.
export type RegionCode = '253' | '206' | '425'

export interface RegionMeta {
  code: RegionCode
  /** Human-readable area name, e.g. "Seattle". */
  name: string
  /** Short descriptor used in copy, e.g. "Seattle proper". */
  label: string
  /** Representative cities for the region. */
  cities: string[]
  /** Map center coordinates [lat, lng] for the region. */
  center: [number, number]
  /** Default delivery zone for the region. */
  defaultZone: number
}

export const REGIONS: Record<RegionCode, RegionMeta> = {
  '253': {
    code: '253',
    name: 'South Sound',
    label: 'Pierce / South Sound',
    cities: ['Tacoma', 'Puyallup', 'Lakewood', 'Federal Way'],
    center: [47.2529, -122.4443], // Tacoma
    defaultZone: 1,
  },
  '206': {
    code: '206',
    name: 'Seattle',
    label: 'Seattle proper',
    cities: ['Seattle', 'West Seattle', 'Mercer Island'],
    center: [47.6062, -122.3321], // Seattle
    defaultZone: 3,
  },
  '425': {
    code: '425',
    name: 'Eastside',
    label: 'Eastside',
    cities: ['Bellevue', 'Redmond', 'Kirkland', 'Issaquah', 'Sammamish'],
    center: [47.6101, -122.2015], // Bellevue
    defaultZone: 2,
  },
}

const VALID: RegionCode[] = ['253', '206', '425']

function isRegionCode(value: string | null): value is RegionCode {
  return value != null && (VALID as string[]).includes(value)
}

/**
 * Resolve the active region.
 * Priority: ?region= override → hostname match → 253 default.
 */
export function getRegion(): RegionCode {
  if (typeof window === 'undefined') return '253'

  try {
    const override = new URLSearchParams(window.location.search).get('region')
    if (isRegionCode(override)) return override
  } catch {
    // Ignore malformed query strings.
  }

  const host = window.location.hostname.toLowerCase()
  if (host.includes('206gravel')) return '206'
  if (host.includes('425gravel')) return '425'
  return '253'
}

/** Convenience accessor for the active region's metadata. */
export function getRegionMeta(): RegionMeta {
  return REGIONS[getRegion()]
}
